import React, { useEffect, useState } from 'react';
import { apiClient } from '@/api/apiClient';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUpdates() {
  const [info, setInfo] = useState({ version: 'loading...', requires_migration: false, changelog: '/docs/MIGRATION_SUMMARY.md' });
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);

  const loadInfo = async () => {
    setChecking(true);
    try {
      const res = await apiClient.get('/updates/info');
      setInfo(res.data || {});
    } catch (err) {
      console.error(err);
      toast.error('Failed to load update info');
    } finally {
      setChecking(false);
    }
  };

  const runMigrations = async () => {
    setRunning(true);
    try {
      const res = await apiClient.post('/updates/run-migrations');
      toast.success('Migrations completed');
      if (res.data?.output) {
        console.log(res.data.output);
      }
      await loadInfo();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Migration failed');
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    loadInfo();
  }, []);

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Updates</h1>
        <p className="text-slate-600 mt-1">Check app version, changelog, and run pending migrations.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-500" />
            Current Version: {info.version}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadInfo} disabled={checking}>
              {checking ? 'Checking...' : 'Check for updates'}
            </Button>
            <a href={info.changelog} target="_blank" rel="noreferrer">
              <Button variant="ghost">View changelog</Button>
            </a>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {info.requires_migration ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Pending migrations detected. Please run migrations.
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> No pending migrations.
            </div>
          )}

          <Button
            onClick={runMigrations}
            disabled={running}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {running ? 'Running migrations...' : 'Run migrations now'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
